import { every, isEmpty, isEqual, keys } from 'lodash';
import { MINIMUM_COORD_VALUE } from '../constants/croppingConsts';

export const croppingTransformer = cropData => {
    return cropData
        .filter(
            singleRatioCropData =>
                singleRatioCropData.replacedImageUrl ||
                singleRatioCropData.croppedAreaPixels ||
                (!singleRatioCropData.isFromCloudinary &&
                    !isEqual(
                        singleRatioCropData.initialCroppedAreaPixels,
                        singleRatioCropData.cloudinaryInitialCroppedAreaPixels
                    ))
        )
        .map(singleRatioCropData => {
            const croppedAreaPixels =
                singleRatioCropData.replacedImageUrl && !singleRatioCropData.croppedAreaPixels
                    ? null
                    : singleRatioCropData.croppedAreaPixels || singleRatioCropData.initialCroppedAreaPixels;
            return singleRatioCropDataTransformer({
                croppedAreaPixels,
                aspect: singleRatioCropData.aspect,
                replacedImageUrl: singleRatioCropData.replacedImageUrl,
            });
        });
};

const DEFAULT_CROP_AREA_WIDTH = 1200;
const getDefaultHeightFromAspect = ({ width, height }) => (DEFAULT_CROP_AREA_WIDTH * height) / width;

export const singleRatioCropDataTransformer = ({ croppedAreaPixels, aspect, replacedImageUrl }) => {
    if (!aspect) {
        return null;
    }

    if (!croppedAreaPixels) {
        return {
            ratio: aspect,
            area: replacedImageUrl
                ? null
                : {
                      topLeftX: 0,
                      topLeftY: 0,
                      width: DEFAULT_CROP_AREA_WIDTH,
                      height: getDefaultHeightFromAspect(aspect),
                  },
            url: replacedImageUrl,
        };
    }

    return {
        ratio: aspect,
        area: {
            topLeftX: croppedAreaPixels.x,
            topLeftY: croppedAreaPixels.y,
            width: croppedAreaPixels.width,
            height: croppedAreaPixels.height,
        },
        url: replacedImageUrl,
    };
};
const calculateIntersectAreaOfCropAreas = (oldCropArea, newCropArea) => {
    const oldCropBottomRightY = oldCropArea.height + oldCropArea.topLeftY;
    const oldCropBottomRightX = oldCropArea.width + oldCropArea.topLeftX;
    const newCropBottomRightY = newCropArea.height + newCropArea.topLeftY;
    const newCropBottomRightX = newCropArea.width + newCropArea.topLeftX;

    // based on coordinate system, (x1, y1) top left and (x2, y2) bottom right for each crop
    // intersection height = smaller y2 - bigger y1 (the shared length on the y-axis)
    const newCropWithinOldCropHeight = Math.max(
        Math.min(oldCropBottomRightY, newCropBottomRightY) - Math.max(oldCropArea.topLeftY, newCropArea.topLeftY),
        0
    );
    // intersection width = smaller x2 - bigger x2 (the shared length on the x-axis)
    const newCropWithinOldCropWidth = Math.max(
        Math.min(oldCropBottomRightX, newCropBottomRightX) - Math.max(oldCropArea.topLeftX, newCropArea.topLeftX),
        0
    );

    return newCropWithinOldCropHeight * newCropWithinOldCropWidth;
};

export const isSignificantChange = (oldCropData, newCropData, significantChangeThreshold) => {
    if (!oldCropData || isEmpty(oldCropData)) {
        return true;
    }

    const intersectArea = calculateIntersectAreaOfCropAreas(oldCropData.area, newCropData.area);

    const newArea = newCropData.area.height * newCropData.area.width;
    const oldArea = oldCropData.area.height * oldCropData.area.width;

    // if intersection of new and old cropping makes up less than x% of old area or makes up less than x% of new area, then move to pending
    return (
        intersectArea <= oldArea * significantChangeThreshold || intersectArea <= newArea * significantChangeThreshold
    );
};

// if the absolute value of either x or y is less than MINIMUM_COORD_VALUE, then set to 0
export const getSanitizedCrop = ({ x, y }) => {
    return {
        x: Math.abs(x) < MINIMUM_COORD_VALUE ? 0 : x,
        y: Math.abs(y) < MINIMUM_COORD_VALUE ? 0 : y,
    };
};

const isSimilar = (a, b) => Math.abs(a - b) <= 2;
export const isNewCropDataSimilarToInitial = (prevCropData, newCropData) =>
    prevCropData && every(keys(prevCropData), prevKey => isSimilar(prevCropData[prevKey], newCropData[prevKey]));

export const getImageMaxZoomBySize = src => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = src;

        img.onload = () => {
            resolve(Math.max(Math.min(img.width / 600, img.height / 400), 1));
        };

        img.onerror = () => {
            reject(1);
        };
    });
};
